import { useQuery } from "@web-monorepo/fetchers";
import { keyBy } from "es-toolkit/compat";
import { Board, Lesson } from "src/pods/ideas/data/types";

export type UseBoardProps = { board?: Board };

export type UseBoardResult = {
  /** Lessons in board.ideas order, filtered to ones present in the bundled lessons.json. */
  lessons: Lesson[];
};

/**
 * ViewModel for Board: async-loads the bundled `lessons.json`, maps the
 * `board.ideas` slugs to lesson records, and returns them. Returns `[]` until
 * the load completes; the Board component shows a LoadingMojo in that state.
 *
 * Uses `useQuery` to handle the async lifecycle — eliminates the manual
 * useState/useEffect + cancelled-guard pattern (which trips
 * `@web-monorepo/no-setState-in-useEffect`) and gets unmount-safe loading
 * state for free. `staleTime: Infinity` because `lessons.json` is a bundled
 * static asset that never changes at runtime.
 *
 * Storybook stories override `useVm` on Board to supply canned lessons
 * synchronously.
 */
export function useBoard({ board }: UseBoardProps): UseBoardResult {
  const { data: lessons = [] } = useQuery({
    queryKey: ["toolkit-ideas-lessons-json", board?.slug],
    queryFn: async () => {
      // `enabled: Boolean(board)` keeps this from running with no board, but
      // the type can't carry that guarantee — narrow explicitly.
      if (!board) return [];
      const { default: lessonsList } = await import("../../../../../data/lessons.json");
      const lessonsMap: Record<string, Lesson | undefined> = keyBy(lessonsList, "url_slug");
      return board.ideas
        .map(({ url_slug: slug }) => lessonsMap[slug])
        .filter((lesson): lesson is Lesson => lesson !== undefined);
    },
    enabled: Boolean(board),
    staleTime: Infinity,
  });

  return { lessons };
}
